import { Injectable } from '@angular/core'
import { serializeHttpParams } from '@ftr/api-shared'
import {
  CourtroomSession,
  CreateCourtroomSessionBody,
  DeleteCourtroomSessionQuery,
  UpdateCourtroomSessionBody,
  UpdateCourtroomSessionQuery,
} from '@ftr/contracts/regional-api'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, mapData } from '@ftr/foundation'
import { plainToClass } from '@ftr/serialization'
import { LocalDateTime } from '@js-joda/core'
import { switchMap } from 'rxjs'
import { RegionalApiClientSetupService } from '../../api-client'

@Injectable({
  providedIn: 'root',
})
export class CourtroomSessionsService extends RegionalApiClientSetupService {
  getRecordingCourtroomSessions(courtSystemId: Uuid, recordingId: Uuid): ApiResult<CourtroomSession[]> {
    return this.regionalApiClient('/live-stream', courtSystemId).pipe(
      switchMap(client =>
        client.get<CourtroomSession[]>({
          headers: { courtSystemId },
          path: `/recording/${recordingId}/courtroom-session`,
        }),
      ),
      mapData((response: unknown[]) => response.map(item => plainToClass(CourtroomSession, item))),
    )
  }

  addCourtroomSession(
    courtSystemId: Uuid,
    recordingId: Uuid,
    startTime: LocalDateTime,
    endTime: LocalDateTime,
  ): ApiResult<CourtroomSession> {
    return this.regionalApiClient('/live-stream', courtSystemId).pipe(
      switchMap(client =>
        client.post<CourtroomSession>({
          headers: { courtSystemId },
          path: `/recording/${recordingId}/courtroom-session`,
          body: new CreateCourtroomSessionBody(courtSystemId, startTime, endTime),
        }),
      ),
      mapData(response => plainToClass(CourtroomSession, response)),
    )
  }

  deleteCourtroomSession(
    id: Uuid,
    courtSystemId: Uuid,
    recordingId: Uuid,
    conflictResolutionToken?: string,
  ): ApiResult {
    return this.regionalApiClient('/live-stream', courtSystemId).pipe(
      switchMap(client =>
        client.delete<null>({
          headers: { courtSystemId },
          path: `/recording/${recordingId}/courtroom-session/${id}`,
          params: conflictResolutionToken
            ? serializeHttpParams(new DeleteCourtroomSessionQuery(conflictResolutionToken))
            : undefined,
        }),
      ),
    )
  }

  updateCourtroomSession(
    id: Uuid,
    courtSystemId: Uuid,
    recordingId: Uuid,
    startTime: UpdateCourtroomSessionBody['startTime'],
    endTime: UpdateCourtroomSessionBody['endTime'],
    departmentId: Uuid | undefined,
    conflictResolutionToken?: string,
  ): ApiResult<CourtroomSession> {
    return this.regionalApiClient('/live-stream', courtSystemId).pipe(
      switchMap(client =>
        client.patch<null>({
          headers: { courtSystemId },
          path: `/recording/${recordingId}/courtroom-session/${id}`,
          params: conflictResolutionToken
            ? serializeHttpParams(new UpdateCourtroomSessionQuery(conflictResolutionToken))
            : undefined,
          // The endpoint is a PATCH endpoint and allows partial updates (undefined values are unaffected)
          // However the front end behaves more like a PUT, so `undefined` must be explicitly set to `null`
          body: new UpdateCourtroomSessionBody(startTime, endTime, departmentId ?? null),
        }),
      ),
      mapData(response => plainToClass(CourtroomSession, response)),
    )
  }
}
